import type { WorkflowInstanceView } from "@ncp/api-client";

export type AiVerdict = "Satisfied" | "Deficient" | "NeedsInformation" | "NotApplicable" | "Unknown";
export type FindingVerdict = "Pending" | "Satisfied" | "Deficient" | "NeedsInformation" | "NotApplicable";
export type IssueKind = "Deficiency" | "Question" | "Risk" | "FollowUp";
export type IssueSeverity = "Low" | "Medium" | "High" | "Blocking";
export type IssueStatus = "Open" | "Resolved" | "Dismissed";

/** A source an assessment or a finding rests on; the workbench opens it in place. */
export interface ReviewSource { kind: string; id: string; title: string; locator?: string | null; status?: string | null; titleAr?: string | null; locatorAr?: string | null }

export interface AiAssessment {
  verdict: AiVerdict;
  summary: string;
  summaryAr?: string | null;
  confidence: number;
  sources: ReviewSource[];
  proposalId?: string | null;
  proposalStatus?: string | null;
  flags: string[];
  /** Deterministic | Agent | Mixed */
  basis: string;
  /** The agent that can be run to refresh this assessment, when one exists. */
  agentKey?: string | null;
  /** When the agent draft behind the assessment was produced; null for live rule-based assessments. */
  assessedAt?: string | null;
  /** The numbered sources a written assessment cites as `[S n]`. */
  references?: AssessmentReference[] | null;
  /** What a rule's reading rests on (Recorded | RecordedAndRead | IncompleteOrScreen | NotOnFile), which sets its confidence as the published method states it; null for an agent's reading. */
  readingBasis?: string | null;
}

/** A numbered source of a written assessment: a provision of law, a document of the file, a Data Bank series, a precedent or a tool's reading. */
export interface AssessmentReference { n: number; sourceType: "Regulation" | "Document" | "DataBank" | "Precedent" | "ComparativeReference" | "Tool"; sourceId: string; title: string; locator?: string | null; excerpt?: string | null }

/** One turn of a conversation about a suggestion, kept on the case. */
export interface DiscussionTurn { role: "officer" | "platform"; text: string; at: string; byName?: string | null; offline?: boolean }

export interface ReviewFinding {
  verdict: FindingVerdict;
  note?: string | null;
  evidence: ReviewSource[];
  reviewedByName?: string | null;
  reviewedAt?: string | null;
  acceptedAiVerdict: boolean;
  aiVerdictAtReview?: string | null;
  aiSummaryAtReview?: string | null;
  /** Current version number; every change appends a version. */
  version: number;
  /** Whether the officer had opened the platform's assessment when recording. */
  aiRevealed?: boolean;
}

export type FindingChangeKind = "Recorded" | "Updated" | "Cleared" | "Restored";
export type FindingField = "Verdict" | "Note" | "Evidence";

/** One immutable version of a finding. */
export interface ReviewFindingVersion {
  version: number;
  verdict: FindingVerdict;
  note?: string | null;
  evidence: ReviewSource[];
  aiVerdictAtReview?: string | null;
  aiSummaryAtReview?: string | null;
  aiConfidenceAtReview?: number | null;
  aiProposalId?: string | null;
  acceptedAiVerdict: boolean;
  kind: FindingChangeKind;
  changedFields: FindingField[];
  restoredFromVersion?: number | null;
  changedByName: string;
  changedByRole?: string | null;
  changedAt: string;
}

export interface ReviewCriterion {
  key: string;
  title: string;
  titleAr: string;
  guidance: string;
  guidanceAr: string;
  legalBasis?: string | null;
  legalBasisAr?: string | null;
  required: boolean;
  /** Where the evidence lives: file:documents · file:correspondence · file:parties · analysis · analysis:report · … · decision · history:process */
  surface: string;
  aiSource: string;
  ai?: AiAssessment | null;
  finding?: ReviewFinding | null;
}

export interface ReviewIssue {
  id: string;
  criterionKey?: string | null;
  stageKey?: string | null;
  kind: IssueKind;
  severity: IssueSeverity;
  title: string;
  detail?: string | null;
  status: IssueStatus;
  raisedByKind: string;
  raisedByName: string;
  raisedAt: string;
  resolvedAt?: string | null;
  resolvedByName?: string | null;
  resolution?: string | null;
  linkedProposalId?: string | null;
  linkedRfiRequestId?: string | null;
  linkedDocumentId?: string | null;
}

/** A reason the stage cannot be concluded yet. */
export interface ReviewBlocker { code: "RequiredUndecided" | "BlockingIssues" | "NoTransition" | "Terminal" | string; count: number; detail: string }

export interface ReviewProgress {
  total: number;
  decided: number;
  required: number;
  requiredDecided: number;
  agreeWithAi: number;
  overrodeAi: number;
  openIssues: number;
  blockingIssues: number;
  canConclude: boolean;
  blockers: ReviewBlocker[];
}

/** Localised sentence for a blocker (the backoffice `review.blockers.*` keys take `count` and `detail`). */
export function blockerText(b: ReviewBlocker, t: (key: string, vars?: Record<string, unknown>) => string): string {
  if (b.code === "BindingFindings") return t("backoffice:r3Process.blockers.BindingFindings", { count: b.count, detail: b.detail });
  return t(`backoffice:review.blockers.${b.code}`, { count: b.count, detail: b.detail, defaultValue: b.detail });
}

/** An authorised officer's override of a finding of the platform, with reasons (Condition 2.21). */
export interface BindingOverride { id: string; kind: string; subjectKey: string; stageKey: string; finding: string; findingAr?: string | null; reason: string; byName: string; byRole?: string | null; at: string; proposalId?: string | null }

/** A matter the platform determined in the file that rules out the exits in `blockedTriggers` until answered or overridden. */
export interface BindingFinding { kind: string; subjectKey: string; title: string; titleAr: string; detail?: string | null; detailAr?: string | null; blockedTriggers: string[]; overridable: boolean; override?: BindingOverride | null; open: boolean }

export type ReviewTransition = WorkflowInstanceView["availableTransitions"][number];

export interface ReviewView {
  reviewId: string;
  stageKey: string;
  stageLabel: string;
  stageLabelAr: string;
  visit: number;
  status: "Open" | "Concluded" | "Superseded";
  openedAt: string;
  openedByName?: string | null;
  stageLegalBasis?: string | null;
  stageActors: string[];
  stageAgentHooks: string[];
  catalogued: boolean;
  criteria: ReviewCriterion[];
  issues: ReviewIssue[];
  transitions: ReviewTransition[];
  recommendedTrigger?: string | null;
  recommendedByAgent?: string | null;
  progress: ReviewProgress;
  draftRationale?: string | null;
  checklist?: StageChecklist | null;
  binding?: BindingFinding[] | null;
  /** The signed-in officer may override a finding of the platform. */
  mayOverride?: boolean;
  /** The platform's assessment stays folded until the officer records a finding or opens it (Conditions 4.5, 7.12); a platform setting. */
  officerViewFirst?: boolean;
}

export type StageChecklistKind = "Criterion" | "Issue" | "Document" | "Task" | "Draft" | "Condition" | "Finding";

/** Something still to be done in the stage; `target` is `criterion:{key}`, `issue:{id}`, `document:{key}`, `task:{id}`, `proposal:{id}`, `condition:{guard}` or `finding:{kind}|{subject}`. */
export interface StageChecklistItem { kind: StageChecklistKind; key: string; title: string; titleAr: string; detail?: string | null; detailAr?: string | null; blocking: boolean; target: string; /** A workbench where the criterion's work is done. */ surface?: string | null;
  /** A party's request the Department may refuse with reasons as well as confirm (a complainant's withdrawal, Condition 3.2). */ refusable?: boolean }

/** The screen's one primary action, decided on the server from the same checklist the conclusion is validated against. */
export interface StagePrimaryAction { kind: "criterion" | "issue" | "surface" | "finding" | "confirm" | "conclude" | "waiting" | "awaiting" | "none"; target?: string | null; title?: string | null; titleAr?: string | null; surface?: string | null }

/** What is left in the stage, and the one next step for the signed-in officer; `forwardTrigger` is the way out the conclusion offers first. */
/** `outstanding.length` items are left; `needed` of them stop the stage being concluded – every count is a count of what is listed. */
export interface StageChecklist { stageKey: string; reviewOpen: boolean; canAct: boolean; canConclude: boolean; done: number; total: number; outstanding: StageChecklistItem[]; primary: StagePrimaryAction; ownerRole?: string | null; forwardTrigger?: string | null; needed?: number; mayOverride?: boolean }

/** Splits a `finding:{kind}|{subject}` target. */
export function findingOf(target: string): { kind: string; subjectKey: string } | null {
  if (!target.startsWith("finding:")) return null;
  const rest = target.slice("finding:".length);
  const bar = rest.indexOf("|");
  return bar < 0 ? null : { kind: rest.slice(0, bar), subjectKey: rest.slice(bar + 1) };
}

export interface ReviewRecordFinding { criterionKey: string; title: string; titleAr: string; verdict: FindingVerdict; note?: string | null; aiVerdictAtReview?: string | null; acceptedAiVerdict: boolean; reviewedByName?: string | null; reviewedAt?: string | null; version: number }

export interface ReviewRecord {
  id: string;
  stageKey: string;
  stageLabel: string;
  stageLabelAr: string;
  visit: number;
  status: "Open" | "Concluded" | "Superseded";
  openedAt: string;
  openedByName?: string | null;
  concludedAt?: string | null;
  concludedByName?: string | null;
  concludedByRole?: string | null;
  outcomeTrigger?: string | null;
  outcomeLabel?: string | null;
  outcomeLabelAr?: string | null;
  toState?: string | null;
  rationaleMarkdown?: string | null;
  aiRecommendedTrigger?: string | null;
  findings: ReviewRecordFinding[];
  /** How the stage ended: Review (signed off), Step (an officer's step), Party (the applicant or a party), Automatic. */
  endRoute?: string | null;
}

/** The reviewer's verdicts in the order they are offered, with the key that selects each one from the keyboard. */
export const FINDING_VERDICTS: ReadonlyArray<{ value: Exclude<FindingVerdict, "Pending">; hotkey: string }> = [
  { value: "Satisfied", hotkey: "1" },
  { value: "Deficient", hotkey: "2" },
  { value: "NeedsInformation", hotkey: "3" },
  { value: "NotApplicable", hotkey: "4" },
];

/** Whether a human verdict agrees with an AI verdict in substance — mirrors ReviewService.SameDirection on the server. */
export function sameDirection(ai: string | null | undefined, human: FindingVerdict): boolean {
  if (!ai || ai === "Unknown") return true;
  if (ai === human) return true;
  return (ai === "NeedsInformation" && human === "Deficient") || (ai === "Deficient" && human === "NeedsInformation");
}

/** The tab and sub-view a criterion's evidence surface points at. */
export function surfaceTarget(surface: string): { tab: string; view?: string } {
  const [tab, view] = surface.split(":");
  return { tab, view };
}
